import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';

import pansasLogo from '../assets/company-logos/pansas.jpg';
import GoogleLogo from '../assets/company-logos/google_logo.jpeg';
import amazonLogo from '../assets/company-logos/amazon_logo.jpeg';
import mediatekLogo from '../assets/company-logos/mediatek_logo.jpeg';
import appleLogo from '../assets/company-logos/apple_logo.jpeg';
import broadcomLogo from '../assets/company-logos/broadcom_logo.jpeg';
import ciscoLogo from '../assets/company-logos/cisco_logo.jpeg';
import zteLogo from '../assets/company-logos/zte_logo.jpeg';
import samsungLogo from '../assets/company-logos/samsung_logo.png';

// Styled Components
const ExperienceSection = styled.section`
  padding: 100px 10%;
  background-color: var(--color-bg-alt);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 80px 5%;
  }
  
  @media (max-width: 576px) {
    padding: 60px 5%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  text-align: center;
  position: relative;
  font-weight: 600;
  letter-spacing: 0.5px;

  &:after {
    content: '';
    position: absolute;
    width: 50px;
    height: 3px;
    background-color: var(--color-primary);
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 576px) {
    font-size: 1.8rem;
    margin-bottom: 1.2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  color: var(--color-secondary);
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
  font-weight: 300;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 0 auto 3rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    margin: 0 auto 2.5rem;
  }
`;

const ExperienceWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
`;

const TimelineLine = styled(motion.div)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  border-radius: 2px;
  z-index: 1;
  top: 0;
  
  @media (max-width: 768px) {
    left: 25px; /* Move timeline to left side on mobile */
    transform: none;
    width: 3px;
  }
  
  @media (max-width: 576px) {
    left: 20px;
    width: 2px;
  }
`;

const TimelineLogo = styled(motion.div)`
  position: absolute;
  left: 50%;
  margin-left: -40px; /* Half of width (80px/2) to center it */
  top: 30px; 
  z-index: 4;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 4px solid var(--color-primary);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
    border-color: var(--color-secondary);
    animation: pulse 1.5s infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1.15);
    }
    50% {
      transform: scale(1.25);
    }
    100% {
      transform: scale(1.15);
    }
  }
  
  @media (max-width: 768px) {
    left: 25px; /* Align with timeline line */
    margin-left: -25px; /* Half of 50px */
    width: 50px;
    height: 50px;
    border: 3px solid var(--color-primary);
    top: 20px;
    
    &:hover {
      transform: scale(1.1);
    }
  }
  
  @media (max-width: 576px) {
    left: 20px;
    margin-left: -20px; /* Half of 40px */
    width: 40px;
    height: 40px;
    border: 2px solid var(--color-primary);
    top: 15px;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const TimelineLogoImage = styled.img`
  width: 110%;
  height: 100%;
  object-fit: contain;
  border-radius: 50%;
  pointer-events: none; /* Prevent image from interfering with hover */
  transition: all 0.3s ease;
  
  /* Optional: Add a subtle filter effect on hover */
  ${TimelineLogo}:hover & {
    filter: brightness(1.1) saturate(1.2);
  }
`;

const ExperienceItem = styled(motion.div)`
  padding: 2rem;
  background-color: var(--color-card);
  border-radius: 15px;
  box-shadow: 0 8px 25px var(--color-shadow);
  position: relative;
  width: 45%;
  z-index: 2;

  ${props => props.isEven ? `
    margin-left: 0;
    margin-right: auto;
  ` : `
    margin-left: auto;
    margin-right: 0;
  `}
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    top: 45px;
    border: 15px solid transparent;
    z-index: 1;
    
    ${props => props.isEven ? `
      right: -30px;
      border-left-color: var(--color-card);
    ` : `
      left: -30px;
      border-right-color: var(--color-card);
    `}
    
    @media (max-width: 768px) {
      left: -10px !important; 
      right: auto !important;
      border: 8px solid transparent;
      border-right-color: var(--color-card) !important;
      border-left-color: transparent !important;
      top: 25px;
    }
    
    @media (max-width: 576px) {
      left: -8px !important;
      border: 6px solid transparent;
      border-right-color: var(--color-card) !important;
      border-left-color: transparent !important;
      top: 20px;
    }
  }
  
  @media (max-width: 768px) {
    width: calc(100% - 80px); /* More space for content */
    margin-left: 80px !important; /* Space for timeline and logo */
    margin-right: 0 !important;
    padding: 1.5rem;
  }
  
  @media (max-width: 576px) {
    width: calc(100% - 60px); /* Even more space for content */
    margin-left: 60px !important; /* Space for timeline and logo */
    padding: 1.2rem;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 35px var(--color-shadow);
    transition: all 0.3s ease;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.8rem;
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 576px) {
    gap: 0.6rem;
    margin-bottom: 1rem;
  }
`;

const LogoAndDetails = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const ItemTitle = styled.h4`
  font-size: 1.2rem;
  color: var(--color-text);
  margin-bottom: 0.5rem;
  font-weight: 700;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.3rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    margin-bottom: 0.2rem;
  }
`;

const ItemSubtitle = styled.h5`
  font-size: 1.2rem;
  color: var(--color-primary);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 1rem;
    letter-spacing: 0.3px;
  }
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
    letter-spacing: 0.2px;
  }
`;

const ItemPeriod = styled.div`
  font-size: 0.85rem;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 25px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  font-weight: 600;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);

  .icon {
    margin-right: 8px;
    font-size: 0.8rem;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.5rem 1rem;
    align-self: flex-start;
  }
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.8rem;
  }
`;

const ItemDescription = styled.div`
  font-size: 0.95rem;
  color: var(--color-secondary);
  line-height: 1.7;
  margin: 0;
  letter-spacing: 0.3px;
  text-align: left;
  white-space: pre-line; /* This preserves line breaks */
  
  ul {
    margin: 0;
    padding-left: 1.2rem;
    
    li {
      margin-bottom: 0rem;
      line-height: 1.5;
      
      &::marker {
        color: var(--color-primary);
        font-size: 1.1em;
      }
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.6;
    letter-spacing: 0.2px;
    
    ul {
      padding-left: 1rem;
      
      li {
        margin-bottom: 0.6rem;
        line-height: 1.5;
      }
    }
  }
  
  @media (max-width: 576px) {
    font-size: 0.85rem;
    line-height: 1.5;
    letter-spacing: 0.1px;
    
    ul {
      padding-left: 0.8rem;
      
      li {
        margin-bottom: 0.5rem;
        line-height: 1.4;
      }
    }
  }
`;

const ItemWrapper = styled.div`
  position: relative;
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Scroll-triggered animation wrapper
const TimelineItem = ({ item, index, isTimelineInView }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { 
    once: true, 
    threshold: 0.3,
    margin: "-100px 0px"
  });

  const isEven = index % 2 === 0;

  return (
    <ItemWrapper ref={ref}>
        <TimelineLogo
          initial={{ scale: 0, opacity: 0 }}
          animate={isTimelineInView ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: index * 0.15 }}
          whileHover={{
            scale: 1.15,
            transition: { duration: 0.2 }
          }}
          style={item.link ? { cursor: 'pointer' } : {}}
          onClick={() => item.link && window.open(item.link, '_blank')}
        >
          <TimelineLogoImage src={item.logo} alt={`${item.company} logo`} />
        </TimelineLogo>

        <ExperienceItem
          isEven={isEven} 
          initial={{ 
            opacity: 0, 
            y: 50, 
            x: isEven ? -50 : 50, 
            scale: 0.9
          }}
          animate={isInView ? { 
            opacity: 1, 
            y: 0, 
            x: 0,
            scale: 1
          } : {}}
          transition={{ 
            duration: 0.8, 
            delay: 0.1,
            type: "spring",
            stiffness: 100,
            damping: 15
          }}
        >
          <ItemHeader>
            <LogoAndDetails>
              <div>
                <ItemTitle>{item.title}</ItemTitle>
                <ItemSubtitle>{item.company}</ItemSubtitle>
              </div>
            </LogoAndDetails>
            <ItemPeriod>
              <span className="icon"><i className="fas fa-calendar-alt"></i></span>
              {item.period}
            </ItemPeriod>
          </ItemHeader>
          <ItemDescription dangerouslySetInnerHTML={{ __html: item.description }} />
      </ExperienceItem>
    </ItemWrapper>
  );
};

const Experience = () => {
  const timelineRef = useRef(null);
  const isTimelineInView = useInView(timelineRef, { 
    once: true, 
    threshold: 0.1 
  });

  const experienceItems = [
        {
          title: 'Founder and CEO',
          company: 'Stealth AGI Startup',
          logo: pansasLogo,
          link: 'https://pansas.org',
          period: 'Feb 2025 - Present',
          description: `Disrupting three iconic industries with a premium, fun, and accessible AGI-XR platform—unlocking innovation and scaling opportunity at its core!`
        },
        {
          title: 'Head of Pixel Product Engineering',
          company: 'Google',
          logo: GoogleLogo,
          period: 'Jan 2021 - Jul 2025',
          description: `<ul>
            <li>Spearheaded Pixel's global wireless & carrier strategy across the Americas and Asia-Pacific, driving innovation and market expansion. This included pioneering 5G launches in Singapore, Southeast Asia, and India (beating iPhone to market), enabling 4G/IMS for 40+ carriers on budget, and achieving 27% YoY growth across North and South America.</li>
            <li>Optimized Engineering Operations, managing a $9M budget and realizing $2.2M in savings in 2023 by streamlining test plans and workflows.</li>
            <li>Led advanced Tech Differentiation, delivering features like 5G mmWave, CBRS, eSIM, DSDS, and VoNR, while ensuring seamless carrier approvals across PTCRB, GCF, and CTIA for all Pixel devices.</li>
            <li>Fostered High-Impact Teams, promoting ownership, collaboration, and technical excellence to achieve ambitious product and market goals.</li>
          </ul>`
        },
        // {
        //   title: 'Founder and CEO',
        //   company: 'Pansas Corp',
        //   logo: pansasLogo,
        //   period: 'Jun 2018 - Present',
        //   description: `Welcome to PanSas Corp, where innovation meets evolution. Born in India as a franchising powerhouse, we have grown into a multi-industry conglomerate, excelling in real estate, angel investments, and now transforming the future of technology. With our latest venture into AI-powered experiences, we are revolutionizing the way businesses connect with customers. Join us as we shape tomorrow's world with creativity, expertise, and cutting-edge solutions. PanSas Corp—building legacies, driving progress.`
        // },
        {
          title: 'Sr. Manager • Launch TPM • FireTV Products',
          company: 'Amazon',
          logo: amazonLogo,
          period: 'Jan 2020 - Jan 2021',
          description: `<ul>
            <li>Led the end-to-end launch of Fire TV products across international markets, delivering innovation, cost efficiency, and operational excellence, including managing cross-functional teams to launch 6 unique devices and achieving 6% cost savings.</li>
            <li>Pioneered In-Car Fire TV Integration, partnering with Tier-1 automotive suppliers to bring Fire TV to rear-seat entertainment systems.</li>
            <li>Led Groundbreaking Remote Launch, introducing the first Alexa Voice Remote with Partner Buttons—boosting user experience and earning 4+ star customer ratings.</li>
            <li>Expanded into India, orchestrating the launch of Amazon’s first Fire TV Edition smart TVs in the region. Drove execution with rigor: from project scoping and testing to executive updates and feature deployment—ensuring quality and timely delivery.</li>
          </ul>`
        },
        {
          title: 'Regional Head • TAM • Carrier Engineering',
          company: 'MediaTek',      
          logo: mediatekLogo,
          period: 'Nov 2018 - Jun 2019',
          description: 'Served as Regional Carrier Engineering Director overseeing 11 countries across APAC. Established strategic partnerships with major network operators, OEMs, and ODMs. Directed certification processes for wireless chips, consumer devices, and wearables, enabling market access across diverse regional regulatory landscapes.'
        },   
        {
          title: 'APAC Regional Engineering Lead',
          company: 'Apple',
          logo: appleLogo,
          period: 'Aug 2014 - Nov 2018',
          description:  `<ul>
            <li>Led wireless innovation and systems engineering across APAC for Apple’s flagship products (iPhone, iPad, Apple Watch), ensuring excellence in connectivity, compliance, and performance.</li>
            <li>Spearheaded ultra-confidential projects, integrating next-gen wireless technologies into the Apple ecosystem.</li>
            <li>Co-inventor of 30+ patents, including two granted US patents (US10642385B1, USP34897AP1) and recipient of Apple’s Hardware Innovation Award (2016).</li>
            <li>Oversaw design, OTA evaluations, and early-stage hardware assessments—defining 3GPP KPIs, protocol conformance, and certification criteria for global network operators.</li>
            <li>Built and mentored multidisciplinary teams, fostering deep technical excellence and global product impact.</li>
          </ul>`
        },
        {
          title: 'Test Engineering Manager',
          company: 'Samsung',
          logo: samsungLogo,
          period: 'Nov 2013 - Aug 2014',
          description: `<ul>
            <li>Led IoT test team to verify LTE Release 10 and next-generation features with LTE chipsets and handsets.</li>
            <li>Expertise in VoLTE, SPS, CQI, TPC, QoS, Carrier Aggregation, CSFB, SRVCC, and more.</li>
            <li>Spearheaded the setup of LTE Smart Labs at Samsung: Conducted profiling for OTT applications, LTE smartphone battery life testing, performance analysis of various applications, and assessed the impact on the network due to each application, including the percentage of signaling overhead generated.</li>
          </ul>`
        },
        {
          title: 'Principal Engineer • Team Lead',
          company: 'Broadcom Inc',
          logo: broadcomLogo,
          period: 'Aug 2011 - Nov 2013',
          description: 'Directed development and successful delivery of Broadcom\'s first Trimode thin modem chipset supporting LTE, UMTS and GSM technologies. Led the modem optimization team to achieve industry-leading performance metrics. Pioneered VoLTE integration, setting new company standards for engineering excellence and cross-functional collaboration.'
        },
        {
          title: 'Sr. Systems Engineer',
          company: 'Cisco',
          logo: ciscoLogo,
          period: 'May 2006 - Jun 2009',
          description: 'Managed system-level testing and validation for Mobile WiMax technologies, focusing on interoperability, security, and performance optimization. Led field testing initiatives for critical network deployments, working directly with key customers to replicate and resolve complex throughput and performance challenges.'
        },
        {
          title: 'Wireless Systems and UAT Engineer',
          company: 'ZTE Corporation',
          logo: zteLogo,
          period: 'May 2004 - May 2006',
          description: 'Directed RAN and EvDO user acceptance validation for major telecom clients including Reliance InfoComm and BSNL. Oversaw commissioning and configuration of Radio Access Network and Core Network equipment. Led drive testing initiatives for network coverage, handoff optimization, and packet data performance validation.'
        }
      ];

  return (
    <ExperienceSection id="experience">
      <SectionTitle>Career Evolution</SectionTitle>
      <SectionSubtitle>
        What I achieved. Who I inspired. What we brought to life!
      </SectionSubtitle>
      <ExperienceWrapper ref={timelineRef}>
        <TimelineLine
          initial={{ scaleY: 0, opacity: 0 }}
          animate={isTimelineInView ? { scaleY: 1, opacity: 1 } : {}}
          transition={{ duration: 1.5, ease: "easeOut" }}
          style={{ transformOrigin: "top center" }}
        />
        {experienceItems.map((item, index) => (
          <TimelineItem 
            key={index}
            item={item} 
            index={index}
            isTimelineInView={isTimelineInView}
          />
        ))}
      </ExperienceWrapper>
    </ExperienceSection>
  );
};

export default Experience;